// Copyright 2025-2026 NXGN Management, LLC. All Rights Reserved.
import { useState, useMemo } from 'react'
import { useQuery } from 'react-query'
import {
  Accordion,
  Box,
  ColumnDef,
  Flex,
  Modal,
  SortingState,
  Table,
  Typography,
  UseShowHide,
} from '@teamnine/pcj-ui-v2'
import { Notes, Storage, Group } from '@/utils/material-icons'
import { useTheme } from 'styled-components'

import {
  getObjectDataDeployments,
  getTemplateFragmentDeployments,
  getTenantDestructions,
  getUserProvisioningDeployments,
  ObjectDataDeployment,
  TemplateFragmentDeployment,
  TenantDestruction,
  UserProvisioningDeployment,
} from '@/services/provisioning'
import { Tenant as ITenant } from '@/services/practices'
import { useErrorHelper } from '@/app/shared/hooks/useErrorHelper'
import { formatName } from '@/utils/helpers'
import StatusTag from '@/app/shared/components/StatusTag'

type ProvisioningLogModalProps = {
  modal: UseShowHide
  practice?: ITenant
}

export default function ProvisioningLogModal({
  modal,
  practice,
}: ProvisioningLogModalProps) {
  const { basicErrorHandler } = useErrorHelper()
  const { colors } = useTheme()
  const practiceId = practice?.id ?? ''

  const fragmentsQuery = useQuery(
    ['templateFragmentDeployments', practiceId, practice?.archived],
    async () => {
      if (!practiceId)
        return [] as (TemplateFragmentDeployment | TenantDestruction)[]
      if (practice?.archived) {
        return getTenantDestructions(practiceId)
      } else {
        return getTemplateFragmentDeployments(practiceId)
      }
    },
    {
      enabled: modal.visible && !!practiceId,
      onError: basicErrorHandler,
      staleTime: 1000 * 60,
      refetchOnWindowFocus: false,
    }
  )

  const objectDataQuery = useQuery(
    ['objectDataDeployments', practiceId],
    async () => {
      if (!practiceId) return [] as ObjectDataDeployment[]
      return getObjectDataDeployments(practiceId)
    },
    {
      enabled:
        modal.visible &&
        !!practiceId &&
        !!practice?.includeObjectDataFromTemplate,
      onError: basicErrorHandler,
      refetchOnWindowFocus: false,
    }
  )

  const usersQuery = useQuery(
    ['userProvisioningDeployments', practiceId],
    async () => {
      if (!practiceId) return [] as UserProvisioningDeployment[]
      return getUserProvisioningDeployments(practiceId)
    },
    {
      enabled: modal.visible && !!practiceId,
      onError: basicErrorHandler,
      refetchOnWindowFocus: false,
    }
  )

  const [configSort, setConfigSort] = useState<SortingState>([])
  const [objectSort, setObjectSort] = useState<SortingState>([])
  const [userSort, setUserSort] = useState<SortingState>([])

  const isLoading =
    fragmentsQuery.isLoading ||
    objectDataQuery.isLoading ||
    usersQuery.isLoading

  const sortedConfigData = useMemo(() => {
    const fragmentDeployments = fragmentsQuery.data ?? []
    if (!fragmentDeployments.length) return fragmentDeployments
    if (!configSort.length) return fragmentDeployments

    const { id } = configSort[0]
    const sorted = [...fragmentDeployments].sort((a, b) => {
      const byName = (a.name ?? '').localeCompare(b.name ?? '', undefined, {
        numeric: true,
        sensitivity: 'base',
      })

      const comparators: Record<string, number> = {
        name: byName,
        status: Number(a.status ?? 0) - Number(b.status ?? 0),
        errorMessage: (a.errorMessage ?? '').localeCompare(
          b.errorMessage ?? '',
          undefined,
          { numeric: true, sensitivity: 'base' }
        ),
      }

      return comparators[id] ?? 0
    })

    return configSort[0].desc ? sorted.reverse() : sorted
  }, [fragmentsQuery.data, configSort])

  const sortedObjectData = useMemo(() => {
    const objectDataDeployments = objectDataQuery.data ?? []
    if (!objectDataDeployments.length) return objectDataDeployments
    if (!objectSort.length) return objectDataDeployments

    const { id, desc } = objectSort[0]

    const sorted = [...objectDataDeployments].sort((a, b) => {
      const byModel = (a.model ?? '').localeCompare(b.model ?? '', undefined, {
        numeric: true,
        sensitivity: 'base',
      })

      const comparators: Record<string, number> = {
        model: byModel,
        records: (a.numberRecords ?? 0) - (b.numberRecords ?? 0),
        status: Number(a.status ?? 0) - Number(b.status ?? 0),
      }

      return comparators[id] ?? 0
    })

    return desc ? sorted.reverse() : sorted
  }, [objectDataQuery.data, objectSort])

  const sortedUserProvisioningData = useMemo(() => {
    const userProvisioningDeployments = usersQuery.data ?? []
    if (!userProvisioningDeployments.length) return userProvisioningDeployments
    if (!userSort.length) return userProvisioningDeployments

    const { id, desc } = userSort[0]

    const sorted = [...userProvisioningDeployments].sort((a, b) => {
      const emailA = a.demoUserEmail ?? ''
      const emailB = b.demoUserEmail ?? ''
      const byEmail = emailA.localeCompare(emailB, undefined, {
        numeric: true,
        sensitivity: 'base',
      })

      const comparators: Record<string, number> = {
        email: byEmail,
        status: Number(a.status ?? 0) - Number(b.status ?? 0),
        errorMessage: (a.errorMessage ?? '').localeCompare(
          b.errorMessage ?? '',
          undefined,
          { numeric: true, sensitivity: 'base' }
        ),
      }

      return comparators[id] ?? 0
    })

    return desc ? sorted.reverse() : sorted
  }, [usersQuery.data, userSort])

  const configColumns: ColumnDef<
    TemplateFragmentDeployment | TenantDestruction
  >[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'Service',
        accessorKey: 'name' as const,
        enableSorting: true,
        cell: ({ row }) => (
          <Typography>{formatName(row.original.name)}</Typography>
        ),
      },
      {
        id: 'errorMessage',
        header: 'Message',
        accessorKey: 'errorMessage' as const,
        enableSorting: true,
        cell: ({ row }) => <Typography>{row.original.errorMessage}</Typography>,
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'status' as const,
        enableSorting: true,
        cell: ({ row }) => <StatusTag status={row.original.status} />,
      },
    ],
    []
  )

  const objectDataColumns: ColumnDef<ObjectDataDeployment>[] = useMemo(
    () => [
      {
        id: 'model',
        header: 'Object',
        accessorKey: 'model' as const,
        enableSorting: true,
        cell: ({ row }) => (
          <Typography color={colors.ngprimary950}>
            {row.original.model}
          </Typography>
        ),
      },
      {
        id: 'records',
        header: 'Records',
        accessorKey: 'numberRecords' as const,
        enableSorting: true,
        cell: ({ row }) => (
          <Typography>{row.original.numberRecords}</Typography>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'status' as const,
        enableSorting: true,
        cell: ({ row }) => <StatusTag status={row.original.status} />,
      },
    ],
    [colors.ngprimary950]
  )

  const userProvisioningColumns: ColumnDef<UserProvisioningDeployment>[] =
    useMemo(
      () => [
        {
          id: 'email',
          header: 'Email',
          accessorKey: 'demoUserEmail' as const,
          enableSorting: true,
          cell: ({ row }) => (
            <Typography>{row.original.demoUserEmail}</Typography>
          ),
        },
        {
          id: 'status',
          header: 'Status',
          accessorKey: 'status' as const,
          enableSorting: true,
          cell: ({ row }) => <StatusTag status={row.original.status} />,
        },
        {
          id: 'errorMessage',
          header: 'Message',
          accessorKey: 'errorMessage' as const,
          enableSorting: true,
          cell: ({ row }) => (
            <Typography>{row.original.errorMessage}</Typography>
          ),
        },
      ],
      []
    )

  return (
    <Modal open={modal.visible} onOpenChange={modal.toggle}>
      <Modal.Display aria-label="Provisioning Log">
        <Modal.Header title="Provisioning Log" />
        <Modal.Content>
          <Flex direction="column" gap={16}>
            <Accordion title="Configuration" defaultExpanded>
              <Box>
                <Table
                  data={sortedConfigData}
                  columns={configColumns}
                  isLoading={isLoading}
                  empty={{
                    title: 'There are no provisioned services.',
                    Icon: Notes,
                  }}
                  sorting={{
                    state: configSort,
                    onSortingChange: setConfigSort,
                  }}
                />
              </Box>
            </Accordion>
            <Accordion title="Data">
              <Box>
                <Table
                  data={sortedObjectData}
                  columns={objectDataColumns}
                  isLoading={isLoading}
                  empty={{
                    title: 'There is no object data.',
                    Icon: Storage,
                  }}
                  sorting={{
                    state: objectSort,
                    onSortingChange: setObjectSort,
                  }}
                />
              </Box>
            </Accordion>
            <Accordion title="Users">
              <Box>
                <Table
                  data={sortedUserProvisioningData}
                  columns={userProvisioningColumns}
                  isLoading={isLoading}
                  empty={{
                    title: 'There are no user provisioning records.',
                    Icon: Group,
                  }}
                  sorting={{
                    state: userSort,
                    onSortingChange: setUserSort,
                  }}
                />
              </Box>
            </Accordion>
          </Flex>
        </Modal.Content>
        <Modal.Footer
          footerAction={{
            primaryButton: {
              label: 'Close',
              closeModal: true,
            },
          }}
        />
      </Modal.Display>
    </Modal>
  )
}
